import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Badge from "react-bootstrap/esm/Badge";
import Button from "react-bootstrap/esm/Button";
import Card from "react-bootstrap/esm/Card";
import Col from "react-bootstrap/esm/Col";
import Dropdown from "react-bootstrap/esm/Dropdown";
import Form from "react-bootstrap/esm/Form";
import Row from "react-bootstrap/esm/Row";
import Spinner from "react-bootstrap/esm/Spinner";
import Table from "react-bootstrap/esm/Table";
import ChoirSong from "../../DTOs/ChoirSong";
import { loadChoirSongs, updateChoirSong } from "../../services/choirSongService";
import {
    Besetzung,
    EMPTY_FILTER,
    filterSongs,
    RepertoireFilter,
    RepertoireSort,
    sortSongs,
    tagsOf,
    toIsoDate,
    usageOf,
} from "../../utils/repertoire";
import RepertoireStats from "./RepertoireStats";
import SongEditModal from "./SongEditModal";
import SongMergeModal from "./SongMergeModal";

interface RepertoireProps {
    gemeindeId: string;
    notify: (variant: string, message: string) => void;
}

const BESETZUNGEN: Besetzung[] = ["chor", "solo", "orchester"];
const COLUMNS: { sort: RepertoireSort; label: string; className?: string }[] = [
    { sort: "name", label: "repertoire.title" },
    { sort: "songbook", label: "repertoire.songbookShort" },
    { sort: "lastSung", label: "repertoire.lastSung" },
    { sort: "count", label: "repertoire.lastYear", className: "text-end" },
];

/** The song catalogue of a Gemeinde: search, filter, edit, merge, archive. */
export default function Repertoire({ gemeindeId, notify }: RepertoireProps) {
    const { t, i18n } = useTranslation();
    const today = useMemo(() => toIsoDate(new Date()), []);
    const [songs, setSongs] = useState<ChoirSong[]>([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState<RepertoireFilter>(EMPTY_FILTER);
    const [sort, setSort] = useState<RepertoireSort>("name");
    const [descending, setDescending] = useState(false);
    const [editing, setEditing] = useState<ChoirSong | null>(null);
    const [showEdit, setShowEdit] = useState(false);
    const [merging, setMerging] = useState<ChoirSong | null>(null);
    const [busyId, setBusyId] = useState("");

    const formatDate = useCallback(
        (date: string) =>
            new Date(`${date}T00:00:00`).toLocaleDateString(i18n.language.startsWith("de") ? "de-DE" : "en-US", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
            }),
        [i18n.language]
    );

    const reload = useCallback(async () => {
        try {
            setSongs(await loadChoirSongs(gemeindeId));
        } catch (err: any) {
            notify("danger", err?.message || t("errors.anErrorOccurred"));
        } finally {
            setLoading(false);
        }
    }, [gemeindeId, notify, t]);

    useEffect(() => {
        setLoading(true);
        reload();
    }, [reload]);

    const visible = useMemo(
        () => sortSongs(filterSongs(songs, filter, today), sort, descending, today),
        [songs, filter, sort, descending, today]
    );
    const tags = useMemo(() => tagsOf(songs), [songs]);
    const unverifiedCount = useMemo(() => songs.filter((song) => song.unverified && !song.archived).length, [songs]);
    // Without performance dates on any song the choir backfill has not run yet.
    const datesMissing = songs.length > 0 && songs.every((song) => song.performanceDates === undefined);

    const changeSort = (column: RepertoireSort) => {
        if (column === sort) {
            setDescending(!descending);
        } else {
            setSort(column);
            // Dates and counts are most useful newest/highest first.
            setDescending(column === "lastSung" || column === "count");
        }
    };

    const patch = async (song: ChoirSong, fields: { archived?: boolean; unverified?: boolean }, message: string) => {
        setBusyId(song.id);
        try {
            await updateChoirSong(gemeindeId, song.id, fields);
            setSongs((current) =>
                current.map((s) => (s.id === song.id ? Object.assign(new ChoirSong(s.name, s.executionType, s.sourceUrl), s, fields) : s))
            );
            notify("success", message);
        } catch (err: any) {
            notify("danger", err?.message || t("errors.anErrorOccurred"));
        } finally {
            setBusyId("");
        }
    };

    const afterChange = (message: string) => {
        setShowEdit(false);
        setMerging(null);
        notify("success", message);
        reload();
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="text-muted mt-2">{t("common.loading")}</p>
            </div>
        );
    }

    return (
        <>
            <RepertoireStats
                gemeindeId={gemeindeId}
                songs={songs}
                today={today}
                formatDate={formatDate}
                onError={(message) => notify("danger", message)}
            />

            <Card className="mb-4 shadow-sm border-0">
                <Card.Body className="py-3">
                    <Row className="align-items-end g-3">
                        <Col xs={12} md={4}>
                            <Form.Label className="small text-muted mb-1" htmlFor="repertoire.search">{t("common.search")}</Form.Label>
                            <Form.Control
                                id="repertoire.search"
                                type="search"
                                value={filter.search}
                                placeholder={String(t("repertoire.searchPlaceholder"))}
                                onChange={(e) => setFilter({ ...filter, search: e.target.value })}
                            />
                        </Col>
                        <Col xs={6} md={2}>
                            <Form.Label className="small text-muted mb-1" htmlFor="repertoire.besetzung">{t("repertoire.besetzung")}</Form.Label>
                            <Form.Select
                                id="repertoire.besetzung"
                                value={filter.besetzung}
                                onChange={(e) => setFilter({ ...filter, besetzung: e.target.value as Besetzung | "" })}
                            >
                                <option value="">{t("repertoire.all")}</option>
                                {BESETZUNGEN.map((key) => <option key={key} value={key}>{t(`repertoire.${key}`)}</option>)}
                            </Form.Select>
                        </Col>
                        <Col xs={6} md={2}>
                            <Form.Label className="small text-muted mb-1" htmlFor="repertoire.tag">{t("repertoire.tag")}</Form.Label>
                            <Form.Select id="repertoire.tag" value={filter.tag} onChange={(e) => setFilter({ ...filter, tag: e.target.value })}>
                                <option value="">{t("repertoire.all")}</option>
                                {tags.map((tag) => <option key={tag} value={tag}>{tag}</option>)}
                            </Form.Select>
                        </Col>
                        <Col xs={12} md={4} className="text-md-end">
                            <Button onClick={() => { setEditing(null); setShowEdit(true); }}>{t("repertoire.newSong")}</Button>
                        </Col>
                    </Row>
                    <div className="d-flex flex-wrap gap-3 mt-3">
                        <Form.Check
                            type="switch"
                            id="repertoire.longUnsung"
                            label={t("repertoire.filterLongUnsung")}
                            checked={filter.longUnsung}
                            onChange={(e) => setFilter({ ...filter, longUnsung: e.target.checked })}
                        />
                        <Form.Check
                            type="switch"
                            id="repertoire.unverified"
                            label={t("repertoire.filterUnverified", { count: unverifiedCount })}
                            checked={filter.unverifiedOnly}
                            onChange={(e) => setFilter({ ...filter, unverifiedOnly: e.target.checked })}
                        />
                        <Form.Check
                            type="switch"
                            id="repertoire.archived"
                            label={t("repertoire.filterArchived")}
                            checked={filter.showArchived}
                            onChange={(e) => setFilter({ ...filter, showArchived: e.target.checked })}
                        />
                    </div>
                </Card.Body>
            </Card>

            {datesMissing && <p className="text-muted small">{t("repertoire.datesMissing")}</p>}
            <p className="text-muted small mb-2">{t("repertoire.count", { shown: visible.length, total: songs.length })}</p>

            <Table hover responsive className="align-middle">
                <thead>
                    <tr>
                        {COLUMNS.map((column) => (
                            <th
                                key={column.sort}
                                className={column.className}
                                aria-sort={sort === column.sort ? (descending ? "descending" : "ascending") : undefined}
                            >
                                <Button variant="link" className="p-0 text-decoration-none fw-semibold text-reset text-nowrap" onClick={() => changeSort(column.sort)}>
                                    {t(column.label)}{sort === column.sort ? (descending ? " ▾" : " ▴") : ""}
                                </Button>
                            </th>
                        ))}
                        <th>{t("repertoire.besetzung")}</th>
                        <th className="text-end">{t("admin.actions")}</th>
                    </tr>
                </thead>
                <tbody>
                    {visible.length === 0 && (
                        <tr><td colSpan={6} className="text-center text-muted py-4">{t("admin.noSongsFound")}</td></tr>
                    )}
                    {visible.map((song) => {
                        const usage = usageOf(song, today);
                        return (
                            <tr key={song.id} className={song.archived ? "text-muted" : undefined}>
                                <td style={{ minWidth: "14rem" }}>
                                    <span className="fw-semibold">{song.name.toString()}</span>
                                    {song.unverified && <Badge bg="warning" className="ms-2">{t("repertoire.unverified")}</Badge>}
                                    {song.archived && <Badge bg="secondary" className="ms-2">{t("repertoire.archived")}</Badge>}
                                    {song.tags.map((tag) => <Badge key={tag} bg="secondary" className="ms-1 border">{tag}</Badge>)}
                                    {song.sheetMusicUrl && (
                                        <a href={song.sheetMusicUrl} target="_blank" rel="noopener noreferrer" className="ms-2 small">{t("repertoire.sheetMusic")}</a>
                                    )}
                                    {song.notes && <div className="small text-muted">{song.notes}</div>}
                                </td>
                                <td>{song.songbookNumber || "–"}</td>
                                <td className="text-nowrap">
                                    {usage.lastSung ? formatDate(usage.lastSung) : "–"}
                                    {usage.nextPlanned && <div className="small text-muted">{t("repertoire.planned", { date: formatDate(usage.nextPlanned) })}</div>}
                                </td>
                                <td className="text-end">{usage.lastYearCount}</td>
                                <td className="small">
                                    {BESETZUNGEN.filter((key) => song.executionType?.[key]).map((key) => t(`repertoire.${key}`)).join(", ") || "–"}
                                </td>
                                <td className="text-end text-nowrap">
                                    {song.unverified && (
                                        <Button size="sm" variant="outline-success" className="me-1" disabled={busyId === song.id}
                                            onClick={() => patch(song, { unverified: false }, t("repertoire.confirmed", { name: song.name.toString() }))}>
                                            {t("repertoire.confirm")}
                                        </Button>
                                    )}
                                    <Button size="sm" variant="outline-primary" className="me-1" onClick={() => { setEditing(song); setShowEdit(true); }}>
                                        {t("admin.edit")}
                                    </Button>
                                    <Dropdown as="span" align="end">
                                        <Dropdown.Toggle size="sm" variant="outline-secondary" disabled={busyId === song.id}
                                            aria-label={String(t("repertoire.moreActions", { name: song.name.toString() }))}>
                                            {t("repertoire.moreShort")}
                                        </Dropdown.Toggle>
                                        {/* fixed: the scrolling table wrapper would clip the menu */}
                                        <Dropdown.Menu popperConfig={{ strategy: "fixed" }} renderOnMount>
                                            <Dropdown.Item as="button" onClick={() => setMerging(song)}>{t("repertoire.merge")}</Dropdown.Item>
                                            <Dropdown.Item as="button"
                                                onClick={() => patch(
                                                    song,
                                                    { archived: !song.archived },
                                                    t(song.archived ? "repertoire.restored" : "repertoire.archivedDone", { name: song.name.toString() })
                                                )}>
                                                {song.archived ? t("repertoire.restore") : t("repertoire.archive")}
                                            </Dropdown.Item>
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>

            <SongEditModal gemeindeId={gemeindeId} song={editing} show={showEdit} onClose={() => setShowEdit(false)} onSaved={afterChange} />
            <SongMergeModal gemeindeId={gemeindeId} song={merging} songs={songs} onClose={() => setMerging(null)} onMerged={afterChange} />
        </>
    );
}
